import { Info, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/ui';
import { cn } from '@shared/lib/utils';
import { logo as logoLightOnDark } from '@shared/assets';
import logoDarkOnLight from '@shared/assets/icons/logo-dark.svg';
import { useInvoiceTheme } from '../lib/invoice-theme';
import { CopyIconButton } from './copy-icon-button';
import { InvoiceLanguageMenu } from './invoice-language-menu';
import { InvoiceNetworkRow } from './invoice-network-row';

interface InvoicePayCardAProps {
  amount: string;
  currency: string;
  amountUsd: string | null;
  networkCode?: string | null;
  networkLabel?: string | null;
  showNetworkFee?: boolean;
  onCopyAmount: () => void;
  amountCopied?: boolean;
}

export function InvoicePayCardA({
  amount,
  currency,
  amountUsd,
  networkCode,
  networkLabel,
  showNetworkFee = true,
  onCopyAmount,
  amountCopied,
}: InvoicePayCardAProps) {
  const { t } = useTranslation('invoice-pay');
  const { theme, toggleTheme } = useInvoiceTheme();
  const logoSrc = theme === 'dark' ? logoLightOnDark : logoDarkOnLight;
  const showNetwork = Boolean(networkLabel && networkCode);

  return (
    <section
      className={cn(
        'w-full rounded-t-xl border shadow-sm',
        'border-[color:var(--custom-outline)]',
        'border-t-[color:var(--base-input)]',
        'bg-[var(--base-sidebar-background)]',
        'px-5 pb-7 pt-9 sm:px-9'
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <img
          src={logoSrc}
          alt="paygatex"
          width={170}
          height={24}
          className="h-6 w-[170px] object-contain object-left"
        />
        <div className="flex h-5 w-14 items-center gap-4">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-5 shrink-0 rounded-none p-0 text-foreground hover:bg-transparent hover:opacity-70"
            aria-label={t('themeToggle')}
            onClick={toggleTheme}
          >
            {theme === 'light' ? <Sun className="size-5" /> : <Moon className="size-5" />}
          </Button>
          <InvoiceLanguageMenu />
        </div>
      </div>

      <div className="my-7 h-px w-full bg-[var(--base-input)]" aria-hidden />

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCopyAmount}
            className="cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <h1 className="font-sans text-[28px] font-bold leading-10 text-foreground sm:text-4xl">
              {amount} {currency}
            </h1>
          </button>
          <CopyIconButton
            copied={amountCopied}
            onClick={onCopyAmount}
            label={t('copied')}
            className="size-7"
            iconClassName="size-7"
          />
        </div>

        {showNetwork ? (
          <div className="flex flex-col gap-1">
            <p className="font-sans text-sm font-normal tracking-normal text-base-muted-foreground">
              {t('network')}
            </p>
            <InvoiceNetworkRow networkCode={networkCode!} networkLabel={networkLabel!} />
          </div>
        ) : null}

        {amountUsd ? (
          <p className="text-sm font-normal text-base-muted-foreground">
            {t('approxUsd', { amount: amountUsd })}
          </p>
        ) : null}

        {showNetworkFee ? (
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-normal text-base-muted-foreground">
            <Info className="size-4 shrink-0" aria-hidden />
            <span>{t('youPayNetworkFee')}</span>
          </p>
        ) : null}
      </div>
    </section>
  );
}
